// ShareBite Restaurant and NGO registration validation (Task 2.3).
//
// Pure, platform-independent validators for the Restaurant and NGO registration
// payloads. They re-validate every field server-side regardless of any client
// validation, report *every* violated field (not just the first), reject when a
// Profile already exists for the Google UID, and always return the entered
// values so the caller can retain them on rejection.
//
// The validators are decision functions only: they never create a Profile or
// mutate state. The backend RegistrationService consumes a result with
// `accepted === true` to create exactly one Profile; an `accepted === false`
// result creates no Profile and surfaces `violations` to the submitter.
//
// Design reference: design.md "Property 5: Registration validation completeness".
// Requirements:
//   3.1 - Restaurant fields and their bounds.
//   3.3 - Reject and identify each invalid/missing Restaurant field.
//   3.4 - Accept exactly one document of format {PDF, JPEG, PNG}, <= 10 MB.
//   3.5 - Reject when the document violates the format/size constraint.
//   4.1 - NGO fields and their bounds.
//   4.3 - Reject, identify each invalid field, and retain entered NGO values.
//   4.4 - Reject when an NGO Profile already exists for the Google UID.
//   4.5 - Reject when the certificate is not PDF/JPEG/PNG or exceeds 10 MB.
import { FOOD_CATEGORIES } from "./types.js";
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
/** Accepted Verification Document formats (exact, case-sensitive) — Req 3.4/4.5. */
export const ACCEPTED_DOCUMENT_FORMATS = ["PDF", "JPEG", "PNG"];
/** Maximum Verification Document size: 10 MB, inclusive (Req 3.4/4.5). */
export const MAX_DOCUMENT_SIZE_BYTES = 10 * 1024 * 1024;
/** Phone Number digit-count bounds (inclusive) — Req 3.1/4.1. */
export const PHONE_MIN_DIGITS = 10;
export const PHONE_MAX_DIGITS = 15;
/** Restaurant field length bounds (inclusive). */
export const RESTAURANT_NAME_MAX = 100;
export const OWNER_NAME_MAX = 100;
export const RESTAURANT_ADDRESS_MAX = 250;
/** NGO field length bounds (inclusive). */
export const NGO_NAME_MAX = 200;
export const REPRESENTATIVE_NAME_MAX = 100;
export const NGO_ADDRESS_MAX = 300;
/** Food category selection bounds (inclusive). */
export const CATEGORY_MIN = 1;
export const CATEGORY_MAX = 3;
// ---------------------------------------------------------------------------
// Field-level predicates (shared)
// ---------------------------------------------------------------------------
/** A text field is valid when its length is within [1, max] inclusive. */
function isValidText(value, max) {
    return typeof value === "string" && value.length >= 1 && value.length <= max;
}
/** A phone number is valid when it is 10..15 digits with no other characters. */
export function isValidPhone(phone) {
    if (typeof phone !== "string")
        return false;
    if (!/^[0-9]+$/.test(phone))
        return false;
    return phone.length >= PHONE_MIN_DIGITS && phone.length <= PHONE_MAX_DIGITS;
}
/**
 * A category selection is valid when it has 1..3 entries, every entry is an
 * exact (case-sensitive) supported Food Category, and there are no duplicates.
 */
export function isValidCategorySelection(categories) {
    if (!Array.isArray(categories))
        return false;
    if (categories.length < CATEGORY_MIN || categories.length > CATEGORY_MAX) {
        return false;
    }
    const allowed = new Set(FOOD_CATEGORIES);
    for (const c of categories) {
        if (!allowed.has(c))
            return false;
    }
    return new Set(categories).size === categories.length;
}
/**
 * A Verification Document is valid when its format is one of {PDF, JPEG, PNG}
 * (exact, case-sensitive) and its size is at most 10 MB (Req 3.4/4.5).
 */
export function isValidDocument(doc) {
    if (doc == null || typeof doc !== "object")
        return false;
    const formatOk = ACCEPTED_DOCUMENT_FORMATS.includes(doc.format);
    const sizeOk = Number.isFinite(doc.sizeBytes) &&
        doc.sizeBytes >= 0 &&
        doc.sizeBytes <= MAX_DOCUMENT_SIZE_BYTES;
    return formatOk && sizeOk;
}
// ---------------------------------------------------------------------------
// Registration validators
// ---------------------------------------------------------------------------
/**
 * Validate a Restaurant registration payload (Req 3.1/3.3/3.4/3.5).
 *
 * @param payload       The entered registration values.
 * @param profileExists Whether a Profile already exists for the Google UID.
 * @returns A result whose `accepted` flag is `true` iff all fields are valid
 *          and no Profile exists; otherwise `violations` lists every violated
 *          field and `values` echoes the entered payload unchanged.
 */
export function validateRestaurantRegistration(payload, profileExists) {
    const violations = [];
    if (!isValidText(payload.restaurantName, RESTAURANT_NAME_MAX)) {
        violations.push("restaurantName");
    }
    if (!isValidText(payload.ownerName, OWNER_NAME_MAX)) {
        violations.push("ownerName");
    }
    if (!isValidPhone(payload.phoneNumber)) {
        violations.push("phoneNumber");
    }
    if (!isValidText(payload.address, RESTAURANT_ADDRESS_MAX)) {
        violations.push("address");
    }
    if (!isValidCategorySelection(payload.foodCategoriesServed)) {
        violations.push("foodCategoriesServed");
    }
    if (!isValidDocument(payload.document)) {
        violations.push("document");
    }
    if (profileExists) {
        violations.push("duplicateProfile");
    }
    return { accepted: violations.length === 0, violations, values: payload };
}
/**
 * Validate an NGO registration payload (Req 4.1/4.3/4.4/4.5).
 *
 * @param payload       The entered registration values.
 * @param profileExists Whether a Profile already exists for the Google UID.
 * @returns A result whose `accepted` flag is `true` iff all fields are valid
 *          and no Profile exists; otherwise `violations` lists every violated
 *          field and `values` echoes the entered payload unchanged.
 */
export function validateNGORegistration(payload, profileExists) {
    const violations = [];
    if (!isValidText(payload.ngoName, NGO_NAME_MAX)) {
        violations.push("ngoName");
    }
    if (!isValidText(payload.representativeName, REPRESENTATIVE_NAME_MAX)) {
        violations.push("representativeName");
    }
    if (!isValidPhone(payload.phoneNumber)) {
        violations.push("phoneNumber");
    }
    if (!isValidText(payload.address, NGO_ADDRESS_MAX)) {
        violations.push("address");
    }
    if (!isValidCategorySelection(payload.acceptedCategories)) {
        violations.push("acceptedCategories");
    }
    if (!isValidDocument(payload.document)) {
        violations.push("document");
    }
    if (profileExists) {
        violations.push("duplicateProfile");
    }
    return { accepted: violations.length === 0, violations, values: payload };
}
//# sourceMappingURL=registrationValidation.js.map